/**
 * Gate de consentimento (PRD §9.9, §9.10, RGPD Art. 7, ePrivacy).
 *
 * **Nenhum script de analytics ou marketing carrega antes do consentimento.** Sentry (erro, sem PII
 * desde a T0.10) e o gate de versão (`app_settings`, segurança de plataforma) são **necessários** e
 * carregam sempre — pedir consentimento para eles sugeriria que são dispensáveis.
 *
 * ⏳ O Cookiebot é **uma** implementação da fonte de consentimento, não a única: por isso o
 * armazenamento é injetado. O script do serviço entra com a **T0.16**, quando houver `<head>` onde
 * o carregar.
 */

/** As quatro categorias da ePrivacy. Achatar num booleano perderia a granularidade da norma. */
export const CONSENT_CATEGORIES = [
  'necessary',
  'preferences',
  'statistics',
  'marketing',
] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

export type ConsentState = Record<ConsentCategory, boolean>;

export const CONSENT_STORAGE_KEY = 'powerful-consent';

/** Forma mínima exigida do armazenamento — permite testar os casos em que ele falha. */
export interface ConsentStorage {
  getItem(chave: string): string | null;
  setItem(chave: string, valor: string): void;
  removeItem(chave: string): void;
}

/**
 * Estado na ausência de decisão: tudo recusado, exceto o necessário.
 *
 * **Falha fechada.** Presumir aceite quando não há decisão — ou quando a leitura falha — seria a
 * falha mais cara possível aqui: capturar dados de quem nunca consentiu.
 */
const RECUSA_TOTAL: ConsentState = {
  necessary: true,
  preferences: false,
  statistics: false,
  marketing: false,
};

function storagePadrao(): ConsentStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Alguns browsers lançam ao *acessar* `localStorage` com cookies bloqueados.
    return null;
  }
}

/**
 * Lê o consentimento guardado.
 *
 * Qualquer anomalia — ausência, JSON corrompido, tipo errado, armazenamento a lançar — resulta em
 * recusa. Um `throw` aqui derrubaria o arranque do app por causa de uma chave de analytics.
 */
export function readConsent(storage: ConsentStorage | null = storagePadrao()): ConsentState {
  if (!storage) return { ...RECUSA_TOTAL };

  let bruto: string | null;

  try {
    bruto = storage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return { ...RECUSA_TOTAL };
  }

  if (!bruto) return { ...RECUSA_TOTAL };

  let guardado: unknown;

  try {
    guardado = JSON.parse(bruto);
  } catch {
    return { ...RECUSA_TOTAL };
  }

  if (typeof guardado !== 'object' || guardado === null) return { ...RECUSA_TOTAL };

  const registo = guardado as Record<string, unknown>;
  const estado = { ...RECUSA_TOTAL };

  for (const categoria of CONSENT_CATEGORIES) {
    // Só `true` booleano conta como aceite. `"sim"`, `1` ou `"true"` são dados malformados, e
    // aceitá-los abriria a porta a um aceite que ninguém deu.
    if (registo[categoria] === true) estado[categoria] = true;
  }

  // `necessary` nunca é opcional, independentemente do que esteja guardado.
  estado.necessary = true;

  return estado;
}

/**
 * Persiste a decisão.
 *
 * Nunca lança: perder a persistência (quota excedida, modo privado) é aceitável; derrubar o app
 * não é. O consentimento volta ao padrão recusado na sessão seguinte, que é o lado seguro.
 */
export function writeConsent(
  parcial: Partial<ConsentState>,
  storage: ConsentStorage | null = storagePadrao(),
): void {
  if (!storage) return;

  const estado: ConsentState = { ...RECUSA_TOTAL, ...parcial, necessary: true };

  try {
    storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(estado));
  } catch {
    // Sem persistência, mas sem quebrar.
  }
}

/** Limpa a decisão guardada. Usado ao revogar (RGPD Art. 7(3)). */
export function clearConsent(storage: ConsentStorage | null = storagePadrao()): void {
  if (!storage) return;

  try {
    storage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Idem.
  }
}
